import Refresh from '@mui/icons-material/Refresh';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { FC, useState } from 'react';

import { RdConnectInstance } from '@apiContract';
import { Label } from '@components/Label';
import { ClickableBox } from '@uiKit/ClickableBox';
import { InstanceStatus, StatusInstance } from 'src/contexts/RDConnectContext';
import { useMe } from 'src/hooks/useMe';
import { useRDInstance } from 'src/hooks/useRdInstance';

import { sxInstallationCard } from './styles';

const getInstallationName = (inst: RdConnectInstance) => inst.name || `Installation #${inst.id}`;

const getConnectedUserLabel = (inst: RdConnectInstance, currentUserId: string | null) => {
  if (inst.connectedUserId === currentUserId) return 'Connected user: You';
  if (inst.connectedUser?.displayName) return `Connected user: ${inst.connectedUser.displayName}`;
  if (inst.connectedUserId) return 'Connected user: Another user';
  return 'Connected user: None';
};

type Props = {
  onClose: () => void;
};

type InstallationItemProps = {
  inst: StatusInstance;
  isSelected: boolean;
  currentUserId: string | null;
  onSelect: (id: number) => void;
};

const InstallationItem: FC<InstallationItemProps> = ({ inst, isSelected, currentUserId, onSelect }) => {
  const theme = useTheme();
  const isConnectedToMe = inst.instance.connectedUserId === currentUserId;

  return (
    <ClickableBox
      onClick={() => inst.id && onSelect(inst.id)}
      sx={sxInstallationCard(isSelected, isConnectedToMe, theme)}
    >
      <Box>
        <Typography variant="subtitle2">{getInstallationName(inst.instance)}</Typography>
        <Typography variant="body2" color="text.secondary">
          {getConnectedUserLabel(inst.instance, currentUserId)}
        </Typography>
      </Box>
      {!inst.status ? (
        <CircularProgress aria-label="Loading..." size={16} />
      ) : (
        <Label color={inst.status === InstanceStatus.ONLINE ? 'success' : 'error'} variant="soft">
          {inst.status}
        </Label>
      )}
    </ClickableBox>
  );
};

const SelectInstallationDialog: FC<Props> = ({ onClose }) => {
  const { instances, setInstance, isSaving, refresh, isLoading } = useRDInstance();

  const { currentUser } = useMe();
  const currentUserId = currentUser?.id ?? null;

  const [selectedId, setSelectedId] = useState<number | null>(null);

  const handleConnect = async () => {
    await setInstance(selectedId);
    onClose();
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row' }}>
          <span>Select a Received Connect installation</span>
          {!!instances?.length && <Button
            variant="soft"
            size="small"
            startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
            onClick={refresh}
            disabled={isLoading}
          >
            Refresh
          </Button>}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Selecting an installation with a connected user will disconnect that user until they decide to take control
          again.
        </Typography>

        {(instances || []).map((inst) => (
          <InstallationItem
            key={inst.id}
            inst={inst}
            isSelected={selectedId === inst.id}
            currentUserId={currentUserId}
            onSelect={setSelectedId}
          />
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          onClick={handleConnect}
          disabled={!selectedId}
          loading={isSaving}
        >
          Connect
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default SelectInstallationDialog;
